import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAddToCartMutation } from "../api/cartApi";
import { useAppDispatch } from "../../../../common/hooks/hooks";
import { openMultiPanelOffcanvas } from "../../../../common/features/multiPanelOffcanvas/multiPanelOffcanvasSlice";
import { useMutationWithFeedback } from "../../../../common/hooks/useMutationWithFeedback";

/**
 * A custom hook to encapsulate the logic for adding a product to the cart.
 */
export const useProductAddToCart = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [addToCartMutation, mutationResult] = useAddToCartMutation();

  const { trigger, isLoading: isAddingToCart } = useMutationWithFeedback({
    mutation: [addToCartMutation, mutationResult],
    loadingMessage: t(
      "feedback.cart.product.addToCartLoading",
      "Adding to cart..."
    ),
    successMessage: t(
      "feedback.cart.product.addToCartSuccess",
      "Added to cart!"
    ),
    errorMessages: {
      default: t(
        "feedback.cart.product.addToCartError",
        "Failed to add item to cart. Please try again."
      ),
      readonly_quantity: t(
        "feedback.cart.product.soldIndividuallyError",
        "You cannot add another copy of this item to your cart."
      ),
    },
  });

  const handleAddToCart = useCallback(
    async (
      e: React.MouseEvent<HTMLElement> | null, // Allow null for programmatic use
      {
        productId,
        quantity = 1,
        variation,
      }: {
        productId: number;
        quantity?: number;
        variation?: { attribute: string; value: string }[];
      },
      options: { skipOpenPanel?: boolean } = {}
    ) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      // Pass variation to the trigger
      const result = await trigger({ id: productId, quantity, variation });

      // Only open the panel if success AND not skipped
      if (result.success && !options.skipOpenPanel) {
        dispatch(openMultiPanelOffcanvas("cart"));
      }

      return result; // Return result for chaining
    },
    [trigger, dispatch]
  );

  return { handleAddToCart, isAddingToCart };
};
